"use client"

import React from "react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { X } from "lucide-react"
import { BladeStackProvider, useBlades, type BladeRegistry, type BladeRenderProps } from "@/components/blades/blade-stack"
import { ExplorerProvider, useExplorerContext } from "@/components/explorer/explorer-context"
import { useExplorerController } from "@/hooks/use-explorer-controller"
import { useRecordDeepLink } from "@/hooks/use-record-deep-link"
import { EntityRail } from "@/components/explorer/entity-rail"
import { ExplorerToolbar } from "@/components/explorer/explorer-toolbar"
import { ActiveFilterChips } from "@/components/explorer/active-filter-chips"
import { RecordsTable, rowKey } from "@/components/explorer/records-table"
import { ReferenceDataView } from "@/components/explorer/reference-data-view"
import { FilterBlade } from "@/components/explorer/filter-blade"
import { FieldsBlade } from "@/components/explorer/fields-blade"
import { RecordBlade, recordBladeId, type RecordBladeParams } from "@/components/explorer/record-blade"
import { AddToMappingDialog } from "@/components/explorer/add-to-mapping-dialog"
import { AddEntityToMappingDialog } from "@/components/explorer/add-entity-to-mapping-dialog"
import { CreateValueMappingSetDialog } from "@/components/explorer/create-value-mapping-set-dialog"
import { PullBlade, type PullBladeInitial } from "@/components/data-pull/pull-blade"
import { EntityMapBlade } from "@/components/explorer/entity-map/entity-map-blade"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import type { AtsEntityTypeInfo } from "@/types/ats-explorer-api"

/** Blade kinds the explorer opens; params are what each blade needs beyond the shared context */
interface FilterBladeParams { focus?: string }
interface AddFieldParams { path: string; entityType: string | null }
interface AddEntityParams { entity: AtsEntityTypeInfo }
interface ValueSetParams { referenceType: string }
interface PullParams { initial: PullBladeInitial }

const FilterBladeEntry = ({ entry, depth, trail, close, popTo }: BladeRenderProps<FilterBladeParams>) => (
  <FilterBlade open onClose={close} depth={depth} trail={trail} onTrailClick={popTo} focusName={entry.params.focus ?? null} />
)

const FieldsBladeEntry = ({ depth, trail, close, popTo }: BladeRenderProps) => (
  <FieldsBlade open onClose={close} depth={depth} trail={trail} onTrailClick={popTo} />
)

function RecordBladeEntry({ entry, depth, trail, close, popTo }: BladeRenderProps<RecordBladeParams>) {
  const blades = useBlades()
  const { t } = useTranslation()
  const { selectedEntity, entityTypes } = useExplorerContext()
  const params = entry.params
  const isSelectedEntity = selectedEntity?.name === params.entityType
  return (
    <RecordBlade
      {...params}
      knownEntities={entityTypes.map((e) => e.name)}
      open
      onClose={close}
      depth={depth}
      trail={trail}
      onTrailClick={popTo}
      onAddFieldToMapping={(path) => blades.open<AddFieldParams>("add-field", t("explorer.blades.addField"), { path, entityType: params.entityType }, { size: "md" })}
      onAddEntityToMapping={isSelectedEntity && selectedEntity ? () => blades.open<AddEntityParams>("add-entity", t("explorer.blades.addEntity"), { entity: selectedEntity }, { size: "md" }) : undefined}
    />
  )
}

const AddFieldEntry = ({ entry, close }: BladeRenderProps<AddFieldParams>) => (
  <AddToMappingDialog open onOpenChange={(open) => !open && close()} sourceFieldPath={entry.params.path} exploredEntityType={entry.params.entityType} />
)

const AddEntityEntry = ({ entry, close }: BladeRenderProps<AddEntityParams>) => (
  <AddEntityToMappingDialog open onOpenChange={(open) => !open && close()} entityType={entry.params.entity} />
)

const ValueSetEntry = ({ entry, close }: BladeRenderProps<ValueSetParams>) => (
  <CreateValueMappingSetDialog open onOpenChange={(open) => !open && close()} referenceType={entry.params.referenceType} />
)

const PullEntry = ({ entry, depth, trail, close, popTo }: BladeRenderProps<PullParams>) => (
  <PullBlade open onClose={close} depth={depth} trail={trail} onTrailClick={popTo} initial={entry.params.initial} />
)

const EntityMapEntry = ({ depth, trail, close, popTo }: BladeRenderProps) => (
  <EntityMapBlade open onClose={close} depth={depth} trail={trail} onTrailClick={popTo} />
)

const EXPLORER_BLADES: BladeRegistry = {
  "entity-map": (props) => <EntityMapEntry {...(props as BladeRenderProps)} />,
  pull: (props) => <PullEntry {...(props as BladeRenderProps<PullParams>)} />,
  filters: (props) => <FilterBladeEntry {...(props as BladeRenderProps<FilterBladeParams>)} />,
  fields: (props) => <FieldsBladeEntry {...(props as BladeRenderProps)} />,
  record: (props) => <RecordBladeEntry {...(props as BladeRenderProps<RecordBladeParams>)} />,
  "add-field": (props) => <AddFieldEntry {...(props as BladeRenderProps<AddFieldParams>)} />,
  "add-entity": (props) => <AddEntityEntry {...(props as BladeRenderProps<AddEntityParams>)} />,
  "value-set": (props) => <ValueSetEntry {...(props as BladeRenderProps<ValueSetParams>)} />,
}

/**
 * The ATS Entity Explorer: an entity rail, a live-loading record list, and blades for
 * filters, fields, record detail and mapping actions — the page itself never changes.
 */
export function ExplorerPage() {
  const controller = useExplorerController()

  if (controller.atsError) {
    return (
      <div className="container mx-auto py-8">
        <Alert variant="destructive">
          <AlertDescription>{controller.atsError}</AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <ExplorerProvider controller={controller}>
      <BladeStackProvider registry={EXPLORER_BLADES}>
        <ExplorerLayout />
      </BladeStackProvider>
    </ExplorerProvider>
  )
}

function ExplorerLayout() {
  const blades = useBlades()
  const { t } = useTranslation()
  const { atsSystem, entityTypes, selectedEntity, selectEntity, reference, error, clearError, filterDefinitions, filterValues, removeFilter, clearFilters, searchText, setSearchText, selectedFields } = useExplorerContext()

  // ?entity=…&record=… survive a refresh and can be shared
  useRecordDeepLink(entityTypes, selectedEntity, blades.stack, (entity, recordId) => {
    selectEntity(entity)
    if (recordId && atsSystem) {
      blades.open<RecordBladeParams>(
        "record",
        entity.displayName,
        { atsSystem, entityType: entity.name, entityDisplayName: entity.displayName, id: recordId, seed: null, rowIndex: null, supportsDetailFetch: entity.supportsDetailFetch },
        { id: recordBladeId(entity.name, recordId), size: "lg" }
      )
    }
  })

  const openFilters = (focus?: string) => blades.open<FilterBladeParams>("filters", t("explorer.filters.title"), { focus }, { size: "sm" })
  const openFields = () => blades.open("fields", t("explorer.fields.title"), {}, { size: "md" })
  const openAddEntity = () => selectedEntity && blades.open<AddEntityParams>("add-entity", t("explorer.blades.addEntity"), { entity: selectedEntity }, { size: "md" })
  const openValueSet = () => reference.selectedType && blades.open<ValueSetParams>("value-set", t("explorer.blades.valueMapping"), { referenceType: reference.selectedType }, { size: "sm" })
  // This pull continues what is on screen — entity, keyword, filters and columns in their
  // order — and has its own blade, so it never disturbs a pull started from the header.
  const openPull = () => {
    const scope: PullBladeInitial = selectedEntity
      ? { entityName: selectedEntity.name, searchText, filterValues, selectedFields, carriedFromExplorer: true }
      : {}
    blades.open<PullParams>("pull", t("hub.actions.dataPull"), { initial: scope }, { id: "pull-explorer", size: "xl" })
  }

  const openRecord = (record: Record<string, unknown>, rowIndex: number) => {
    if (!atsSystem || !selectedEntity) return
    const id = record.id === undefined || record.id === null ? null : String(record.id)
    blades.open<RecordBladeParams>(
      "record",
      selectedEntity.displayName,
      {
        atsSystem,
        entityType: selectedEntity.name,
        entityDisplayName: selectedEntity.displayName,
        id,
        seed: record,
        rowIndex,
        supportsDetailFetch: selectedEntity.supportsDetailFetch,
      },
      { id: recordBladeId(selectedEntity.name, id, rowIndex), size: "lg" }
    )
  }

  // The row whose blade is at the bottom of the record stack stays highlighted
  const openRecordEntry = blades.stack.find((entry) => entry.kind === "record") as { params: RecordBladeParams } | undefined
  const selectedRowKey =
    openRecordEntry && openRecordEntry.params.entityType === selectedEntity?.name
      ? (openRecordEntry.params.id ?? (openRecordEntry.params.seed && openRecordEntry.params.rowIndex !== null ? rowKey(openRecordEntry.params.seed, openRecordEntry.params.rowIndex) : null))
      : null

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {error && (
        <Alert variant="destructive" className="mx-4 mt-3 flex items-start justify-between gap-3">
          <AlertDescription>{error}</AlertDescription>
          <button type="button" className="text-destructive" aria-label={t("common.actions.dismiss")} onClick={clearError}>
            <X className="h-4 w-4" />
          </button>
        </Alert>
      )}

      <div className="mx-4 mb-4 mt-3 flex min-h-0 flex-1 overflow-hidden rounded-lg border border-card-border bg-card shadow-card">
        <EntityRail onOpenMap={() => blades.open("entity-map", t("explorer.entityMap.title"), {}, { size: "xl" })} />

        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          {reference.selectedType ? (
            <ReferenceDataView onAddValueMapping={openValueSet} />
          ) : selectedEntity ? (
            <>
              <ExplorerToolbar onOpenFilters={() => openFilters()} onOpenFields={openFields} onAddEntityToMapping={openAddEntity} onPull={openPull} />
              <ActiveFilterChips
                definitions={filterDefinitions}
                values={filterValues}
                searchText={searchText}
                onRemove={removeFilter}
                onClearSearch={() => setSearchText("")}
                onClearAll={() => {
                  clearFilters()
                  setSearchText("")
                }}
                onEdit={(name) => openFilters(name)}
              />
              <div className="border-t border-card-border" />
              <RecordsTable selectedRowKey={selectedRowKey} onSelectRecord={openRecord} />
            </>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center text-muted-foreground">
              <p className="font-display text-base font-bold text-foreground">{t("explorer.emptyState.title")}</p>
              <p className="max-w-sm text-sm">{t("explorer.emptyState.description")}</p>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}
